import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';

import { Popover, Text } from '@highlight-cp/core-ui';

import type { AtMentionOption } from '../../types';
import classes from './at-mention-dropdown.module.css';

interface AtMentionDropdownProps {
  options: AtMentionOption[];
  selectedIndex: number;
  position: { x: number; y: number };
  onSelect: (option: AtMentionOption) => void;
}

/**
 * ### AtMentionDropdown
 *
 * @description
 * Dropdown menu that displays @-mention options at the cursor position using Mantine Popover.
 *
 * @param props - Component props
 * @returns React component
 */
const AtMentionDropdown = ({
  options,
  selectedIndex,
  position,
  onSelect,
}: AtMentionDropdownProps) => {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Scroll selected option into view
  useEffect(() => {
    if (dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (options.length === 0) {
    return null;
  }

  const handleOptionClick = (option: AtMentionOption) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(option);
  };

  const handleOptionMouseDown = (e: React.MouseEvent) => {
    // Prevent blur on the input when clicking dropdown items
    e.preventDefault();
  };

  const handleOptionKeyDown = (option: AtMentionOption) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option);
    }
  };

  return (
    <Popover opened withinPortal position="bottom-start" offset={5} width={300}>
      <Popover.Target>
        <div
          className={classes.target}
          style={{ left: `${position.x}px`, top: `${position.y}px` }}
        />
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown} p={0}>
        <div ref={dropdownRef} role="listbox" aria-label={t('Previous answers')}>
          {options.map((option, index) => (
            <div
              key={option.value}
              className={clsx(classes.option, index === selectedIndex && classes.selected)}
              onMouseDown={handleOptionMouseDown}
              onClick={handleOptionClick(option)}
              onKeyDown={handleOptionKeyDown(option)}
              role="option"
              aria-label={option.label}
              aria-selected={index === selectedIndex}
              tabIndex={0}
            >
              <Text size="sm">{option.label}</Text>
            </div>
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};

export default AtMentionDropdown;
